import { Slider, Typography, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import usePlanStore, {
  WeeklyWeight,
} from '../../../../../../../../../store/planStore';
import { TVTouchpointSetup } from '../../../../../../components/Touchpoints/interfaces';
import convertDecimalToPercentValue from '../../../../utils/convertDecimalToPercentValue';

interface WeeklyWeightSliderProps {
  tvTouchpointSetup?: Partial<TVTouchpointSetup>;
  weeklyWeight?: WeeklyWeight;
}

export default function WeeklyWeightSlider({
  tvTouchpointSetup,
  weeklyWeight,
}: WeeklyWeightSliderProps) {
  const { selectedCountry, updateTVConfig, countryForms } = usePlanStore();
  const { t } = useTranslation();

  const handleValuesChange = (newValue: number) => {
    updateTVConfig(selectedCountry!, {
      weeklyWeight: {
        min: weeklyWeight?.min ?? 0,
        max: weeklyWeight?.max ?? 100,
        current: newValue,
      },
    });
  };

  const currentWeight =
    countryForms[selectedCountry!].offline?.tv?.weeklyWeight?.current;

  useEffect(() => {
    const { recoWeeklyGrp } = tvTouchpointSetup ?? {};
    if (recoWeeklyGrp && !currentWeight) {
      handleValuesChange(recoWeeklyGrp);
    }
  }, [tvTouchpointSetup, currentWeight]);

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}
    >
      <Typography variant="caption" lineHeight={1}>
        {`${t('mediaPlanning.weeklyWeight')} (${weeklyWeight?.current ?? 0})`}
      </Typography>
      <Slider
        valueLabelDisplay="auto"
        value={weeklyWeight?.current ?? 40}
        step={1}
        min={weeklyWeight?.min ?? 0}
        max={weeklyWeight?.max ?? 1}
        slots={{
          track:
            (weeklyWeight?.min ?? 0) === (weeklyWeight?.max ?? 1)
              ? () => null
              : undefined,
          thumb:
            (weeklyWeight?.min ?? 0) === (weeklyWeight?.max ?? 1)
              ? () => null
              : undefined,
        }}
        marks={[
          {
            value: weeklyWeight?.min ?? 0,
            label: weeklyWeight?.min,
          },
          ...(weeklyWeight?.min !== weeklyWeight?.max
            ? [
                {
                  value: weeklyWeight?.max ?? 1,
                  label: weeklyWeight?.max,
                },
              ]
            : []),
        ]}
        valueLabelFormat={(value) => value}
        onChange={(_, newValue) => handleValuesChange(+newValue)}
        disabled={weeklyWeight?.min === weeklyWeight?.max}
      />
    </Box>
  );
}
